import Service, { service } from '@ember/service'
import type { CustomMessageInfo } from 'ember-cli-flash/services/flash-messages'
import type FlashMessageService from 'ember-cli-flash/services/flash-messages'
import type { FlashObjectWithOptions } from 'ember-cli-flash/flash/object'
import config from 'teacher-ui-client/config/environment'
import type { SafeString } from '@ember/template'
import type { PhosphorIconName } from 'phosphor-icons'

const isTest = config.environment === 'test'
const TIMEOUT = isTest ? 0 : 10000
const EXTENDED_TIMEOUT = isTest ? 0 : 500

export enum FlashType {
  Positive = 'positive',
  Critical = 'critical',
  Caution = 'caution',
  Info = 'info',
  Default = 'default',
}

interface FlashContent {
  title?: string | SafeString
  subtitle?: string | SafeString
}

interface CustomOptions extends CustomMessageInfo {
  flashType: FlashType
  icon: PhosphorIconName
  title: string | SafeString
  subtitle: string | SafeString
  preventDuplicates?: boolean
}

export type CustomFlashMessageObject = FlashObjectWithOptions & CustomOptions

/**
 * This service handles the queue for flash messages for this application
 *
 * Currently using the service from ember-cli-flash, but we may want to write our own at some point
 * @see https://github.com/poteto/ember-cli-flash
 */
export default class FlashQueueService extends Service {
  @service flashMessages!: FlashMessageService

  // Default args for sticky flashes
  private get stickyArgs() {
    return {
      sticky: true,
      showClose: true,
      destroyOnClick: false,
      extendedTimeout: EXTENDED_TIMEOUT,
    }
  }

  // Default args for auto dismissing messages
  private get nonStickyArgs() {
    return {
      sticky: false,
      showClose: false,
      destroyOnClick: true,
      extendedTimeout: EXTENDED_TIMEOUT,
      timeout: TIMEOUT,
    }
  }

  private addUniqueMessage(options: CustomOptions) {
    /* Need a dupe key for preventDuplicates to work. Currently this key is 'message', so set it to the combined text of
     * title/subtitle. Use toString() in case a htmlsafe string was given.
     */
    this.flashMessages.add({
      ...options,
      message: `${options.flashType || ''}:${options.title.toString()}${options.subtitle.toString()}`,
      preventDuplicates: true,
    })
  }

  get queue(): CustomFlashMessageObject[] {
    return this.flashMessages.queue as CustomFlashMessageObject[]
  }

  clearMessages() {
    this.flashMessages.clearMessages()
  }

  // Green theme, auto-dismiss
  addSuccess(content: FlashContent, customOptions: Partial<CustomOptions> = {}) {
    return this.addPositive(content, customOptions)
  }

  addPositive({ title, subtitle }: FlashContent, customOptions: Partial<CustomOptions> = {}) {
    this.addUniqueMessage({
      icon: 'phosphor-check-bold',
      title: title ?? '',
      subtitle: subtitle ?? '',
      message: '', // will be overridden
      type: '', // we use flashType instead, but is requied for types
      flashType: FlashType.Positive,
      ...this.nonStickyArgs,
      ...customOptions,
    })
  }

  // Red theme
  addFail(content: FlashContent, customOptions: Partial<CustomOptions> = {}) {
    return this.addCritical(content, customOptions)
  }

  addCritical({ title, subtitle }: FlashContent, customOptions: Partial<CustomOptions> = {}) {
    this.addUniqueMessage({
      icon: 'phosphor-warning-fill',
      title: title ?? '',
      subtitle: subtitle ?? '',
      message: '', // will be overridden
      type: '', // we use flashType instead, but is requied for types
      flashType: FlashType.Critical,
      ...this.stickyArgs,
      ...customOptions,
    })
  }

  // Orange theme
  addCaution({ title, subtitle }: FlashContent, customOptions: Partial<CustomOptions> = {}) {
    this.addUniqueMessage({
      icon: 'phosphor-warning-fill',
      title: title ?? '',
      subtitle: subtitle ?? '',
      message: '', // will be overridden
      type: '', // we use flashType instead, but is requied for types
      flashType: FlashType.Caution,
      ...this.stickyArgs,
      ...customOptions,
    })
  }

  // Blue theme
  addInfo({ title, subtitle }: FlashContent, customOptions: Partial<CustomOptions> = {}) {
    this.addUniqueMessage({
      icon: 'phosphor-info-fill',
      title: title ?? '',
      subtitle: subtitle ?? '',
      message: '', // will be overridden
      type: '', // we use flashType instead, but is requied for types
      flashType: FlashType.Info,
      ...this.stickyArgs,
      ...customOptions,
    })
  }
}

declare module '@ember/service' {
  interface Registry {
    'flash-queue': FlashQueueService
  }
}
